<!-- eslint-disable -->

<template>
  <div class="container mt-5">
    <QuoteUserMessages />
    <div v-if="!addingQuoteMessages.length" class="main">
      <h1>Scenarios</h1>
      <TreeTable
        v-model:expandedKeys="expandedKeys"
        :value="treeNodes"
        class=""
      >
        <template #empty>
          <p class="text-center">No scenarios found.</p>
        </template>
        <Column header="Name" style="width: 25%" expander>
          <template #body="{ node }">
            <span v-if="node?.data?.type === 'quote'">
              <router-link
                :to="{
                  name: 'EditQuote',
                  params: { quoteId: node?.data?.data?.id }
                }"
              >
                {{ node?.data?.data?.name }} (V{{
                  node?.data?.data?.version_number
                }})
              </router-link>
            </span>
            <span v-else>
              {{ node?.data?.data?.title }}
            </span>
          </template>
        </Column>
        <Column header="Created At">
          <template #body="{ node }">
            <span v-if="node?.data?.type === 'quote'">
              {{ node?.data?.data?.created_at.slice(0, 10) }}
            </span>
            <span v-else>
              {{ node?.data?.data?.journal_created_at.slice(0, 10) }}
            </span>
          </template>
        </Column>
        <Column field="preparedBy" header="Prepared By"></Column>
        <Column header="Status" style="width: 10%">
          <template #body="{ node }">
            <span v-if="node?.data?.type === 'quote'" class="capitalize">
              {{ node?.data?.data?.status }}
            </span>
          </template>
        </Column>
        <Column>
          <template #header>
            <span>
              <Checkbox
                v-model="allScenarioSelect"
                :inputId="'toggleAll'"
                :value="'all'"
                @change="toggleAllBulkSelects()"
              />
              <label :for="'toggleAll'" class="ml-2">Select All</label>
            </span>
          </template>
          <template #body="{ node }">
            <span v-if="node?.data?.type === 'scenario'">
              <Checkbox
                v-model="selectedScenarios"
                :inputId="node?.data?.data?.id"
                :name="node?.data?.data?.id"
                :value="node?.data?.data"
                @change="updateSelectAllCheckbox"
              />
              <label :for="node?.data?.data?.id" class="ml-2"
                >Select For Bulk Generate Quote</label
              >
            </span>
          </template>
        </Column>
        <Column style="width: 14%">
          <template #body="{ node }">
            <span v-if="node?.data?.type === 'scenario'">
              <Button
                class="mb-3"
                @click="
                  addQuote(
                    node?.data?.data?.id,
                    node?.data?.data?.numQuotes + 1,
                    true,
                    'sizing_single_select'
                  )
                "
                >Create Quote</Button
              >
            </span>
          </template>
        </Column>
      </TreeTable>
      <br />
      <Button @click="addQuotesForAllScenarios()"
        >+ Generate Quotes For All Scenarios</Button
      >
      <Button
        class="ml-3"
        @click="addQuotesForSelectedScenariosToCreateQuotes()"
        >+ Generate Quotes For Selected Scenarios ({{
          selectedScenarios.length
        }})</Button
      >
      <br />
    </div>
    <div v-if="addingQuoteMessages.length" class="main">
      <div class="loading-spinner"></div>
      <h1>...Creating Quote(s)</h1>
      <div v-for="msg in addingQuoteMessages">
        <h2>{{ msg }}</h2>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable */
// @ts-nocheck

import { ref, computed, nextTick } from "vue";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import TreeTable from "primevue/treetable";
import { useToast } from "primevue/usetoast";

import { useRoute, useRouter } from "vue-router";
import {
  type Scenarios,
  type Quotes,
  type SkWellAddress,
  type SkJob,
  useQuoteScenariosBySizingQuery,
  useScenariosToGenerateQuotesQuery,
  useSkEquipmentTypeConversionsQuery,
  // useSkMaterialsQuery,
  useInsertQuoteMutation,
  useSkWellAddressesQuery,
  useUsersQuery,
  useGetSkPlanningOrderQuery,
  useGetQuotesCountQuery
} from "gql/api";

import {
  promiseMap,
  buildMaterialQueryFromSpyglassEquipment,
  buildQuotePlanningOrderQueryString,
  buildQuoteWellAddressQueryString,
  buildSizingQuoteErpHeader,
  materialToQuoteLineItem,
  normalizeErpId,
  pickQuotePlanningOrder,
  fixDate,
  line_item_order
} from "@/utils/quoteHelpers";
import QuoteUserMessages from "@/components/quotes/QuoteUserMessages.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import { LineItemType } from "@/utils/quoteHelpers";
import { useCanonicalNwaLookup } from "@/composables/quote/useCanonicalNwaLookup";

const router = useRouter();
const route = useRoute();
const user = useUserInfo();

if (user.is_customer) {
  nextTick().then(() => router.push({name: "NotFound"}));
}

const sizing_id = route.params.sizingId;

const addingQuoteMessages = ref<string[]>([]);
const scenariosToCreateQuotes = ref<Array<string>>([]);
const selectedScenarios = ref<Array<Scenarios>>([]);
const allScenarioSelect = ref();
const customer_id = ref("");
const well_id = ref("");
const address_id = ref("");
const well_uwi = ref("");
const material_search = ref("");

const toast = useToast();

const { executeQuery: getQuotesCount } = useGetQuotesCountQuery({
  requestPolicy: "network-only",
  pause: true,
  variables: {}
});

const {
  data: quoteScenariosBySizingQuery,
  executeQuery: refetchQuoteScenariosBySizingQuery
} = useQuoteScenariosBySizingQuery({
  pause: !sizing_id,
  requestPolicy: "network-only",
  variables: {
    sizing_id
  }
});

const {
  data: equipmentTypeConversions,
  executeQuery: fetchSKEquipmentTypeConversions
} = useSkEquipmentTypeConversionsQuery({
  requestPolicy: "network-only",
  variables: {
    summitQueryString: ""
  }
});

fetchSKEquipmentTypeConversions();

const fetchSkMaterials = Promise.resolve();

const { executeQuery: fetchERPWellAddress, fetching: fetchingERPWellAddress} = useSkWellAddressesQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    const summitQueryString = buildQuoteWellAddressQueryString({
      addressId: address_id.value,
      customerId: customer_id.value,
      wellId: well_id.value,
      wellUwi: well_uwi.value
    });

    return {
      summitQueryString
    };
  })
});


const { executeQuery: fetchScenariosList } = useScenariosToGenerateQuotesQuery({
  pause: scenariosToCreateQuotes.value.length == 0,
  requestPolicy: "network-only",
  variables: {
    where: {
      id: {
        _in: scenariosToCreateQuotes
      }
    }
  }
});

// SK-5231: the header comes from the customer's planning order, the same
// source the Sales Info form uses. The previous SK jobs lookup used a customer
// filter SK ignores, so quotes took another customer's NWA and price list.
const planningOrderQueryString = ref("");
const { resolveNwa } = useCanonicalNwaLookup();
const { executeQuery: fetchPlanningOrder } = useGetSkPlanningOrderQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => ({
    summitQueryString: planningOrderQueryString.value
  }))
});

const fetchSizingPlanningOrder = (wellAddressData) => {
  const queryString = buildQuotePlanningOrderQueryString({
    addressId: address_id.value || wellAddressData?.address?.id,
    customerId: customer_id.value,
    wellId: well_id.value
  });
  if (!queryString) {
    return Promise.resolve({ planningOrder: null, nwaResolution: null });
  }
  planningOrderQueryString.value = queryString;
  return nextTick()
    .then(() => fetchPlanningOrder())
    .then(resp => pickQuotePlanningOrder(resp?.data?.value?.getSKPlanningOrder))
    // Planning orders carry the NWA name stored on old SK jobs (often a
    // retired ECC name); resolve the id against the canonical NWA instead.
    .then(planningOrder => resolveNwa(planningOrder?.nwa_id)
      .then(nwaResolution => ({ planningOrder, nwaResolution })));
};

const { executeMutation: insertQuote } = useInsertQuoteMutation();

const addQuotesForListScenarios = (list, source) => {
  return promiseMap(list, scenario => {
    return addQuote(scenario.id, scenario.quotes.length + 1, true, source);
  })
    .then(refetchQuoteScenariosBySizingQuery)
    .then(() => {
      addingQuoteMessages.value = [];
    });
};

const addQuotesForAllScenarios = () => {
  toast.add({
    severity: "success",
    summary: "Generating quotes",
    detail: "Generating quotes for all scenarios, please wait...",
    life: 7000
  });
  return addQuotesForListScenarios(
    quoteScenariosBySizingQuery?.value?.scenarios || [],
    "sizing_bulk_select"
  );
};

const addQuotesForSelectedScenariosToCreateQuotes = () => {
  toast.add({
    severity: "success",
    summary: "Generating quotes",
    detail: "Generating quotes for selected scenarios, please wait...",
    life: 7000
  });
  return addQuotesForListScenarios(
    selectedScenarios?.value || [],
    "sizing_multi_select"
  ).then(() => {
    selectedScenarios.value = [];
  });
};

const addQuote = (scenarioId, version, should_route, source) => {
  const etcs = equipmentTypeConversions?.value?.getSKEquipmentTypeConversions;
  addingQuoteMessages.value.push(`Fetching Scenario: ${scenarioId}`);
  scenariosToCreateQuotes.value.push(scenarioId);
  const fetchingScenarios = fetchScenariosList();
  if (source === "sizing_single_select") {
    toast.add({
      severity: "success",
      summary: "Generating quote",
      detail: "Generating quote for selected scenario, please wait...",
      life: 7000
    });
  }

  return fetchingScenarios
    .then(resp => {
      addingQuoteMessages.value.push(
        `Received Data For Scenario: ${scenarioId}`
      );

      // "[TBD]" sizing wells store well_erp_id "0"; treat it as no well.
      address_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.well?.address_erp_id) || "";
      well_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.well?.well_erp_id) || "";
      well_uwi.value =
        resp?.data?.value?.scenarios[0]?.sizing?.well?.well_uwi || "";
      customer_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.customer?.customer_erp_id) || "";

      const quotes = (resp?.data?.value?.scenarios || []).map(scenario => {
        const quoteObj = {};
        addingQuoteMessages.value.push(
          `Fetching Data For Address, Well, and Customer: : ${address_id.value} & ${well_id.value} & ${customer_id.value}`
        );
        addingQuoteMessages.value.push(
          `Fetching PLO Data For Well/Address & Customer.`
        );

        const hasWellOrAddress = Boolean(customer_id.value && (address_id.value || well_id.value));
        const fetchingWellAddress = hasWellOrAddress
          ? nextTick().then(() => fetchERPWellAddress())
          : Promise.resolve(null);

        return fetchingWellAddress.then(_resp => {
          const wellAddressData = _resp?.data?.value?.getSKWellAddresses?.[0];
          addingQuoteMessages.value.push(
            `Received Data For Address: : ${address_id.value || wellAddressData?.address?.id || ""}`
          );

          return Promise.all([
            fetchSizingPlanningOrder(wellAddressData),
            getQuotesCount()
          ]).then(([{ planningOrder, nwaResolution }, quotesCountResp]) => {
            const proposal_count =
              1000001 + (quotesCountResp?.data?.value?.quotes_aggregate?.aggregate?.count ?? 0);
            const skProposalNumber = `SG${proposal_count}`;
            addingQuoteMessages.value.push(
              planningOrder ? `Received Data For PLO.` : `No PLO found for Well/Address & Customer.`
            );
            Object.assign(quoteObj, {
              ...buildSizingQuoteErpHeader({
                addressId: address_id.value,
                customerId: customer_id.value,
                planningOrder,
                today: fixDate(new Date()),
                wellAddress: wellAddressData,
                wellId: well_id.value,
                nwa: nwaResolution,
                sizingCustomerName: scenario?.sizing?.customer?.customer_name,
                sizingWellName: scenario?.sizing?.well?.well_name,
                sizingWellUwi: scenario?.sizing?.well?.well_uwi
              }),
              name: scenario.title,
              // "note": "",
              prepared_by_id: scenario?.author?.id,
              sk_proposal_number: skProposalNumber,
              spyglass_scenario_id: scenario?.id,
              spyglass_scenario_import_json: JSON.stringify(scenario),
              // "sk_job_type": job?.job_type?.material_number || job?.job_type?.material_number,
              version_number: 1,
              source,
              quote_line_items: {
                data: []
              },
              note: scenario?.notes || '',
              comments: scenario?.sizing?.notes || ''
            });
            if (scenario.equipment.r_xref_pumps.length) {
              addingQuoteMessages.value.push(`Adding pumps to quote.`);
            }

            const pumps = scenario.equipment.r_xref_pumps
              .map(pump => {
                const housings_order = pump.housings_order;
                return pump.r_xref_housings.map(housing => {
                  if (housings_order.includes(housing.id)) {
                    return housing;
                  }
                  return null;
                });
              })
              .flat()
              .filter(Boolean);

            return promiseMap(pumps, pump => {
              const materialObjectQuery =
                buildMaterialQueryFromSpyglassEquipment(
                  LineItemType.PUMP,
                  pump,
                  etcs,
                  quoteObj,
                  scenario,
                  material_search
                );
              material_search.value = materialObjectQuery.searchValue;
              addingQuoteMessages.value.push(
                `Fetching pump material: ${material_search.value}`
              );
              return Promise.resolve()
                .then(fetchSkMaterials)
                .then(materialResp => {
                  quoteObj.quote_line_items.data.push(
                    materialToQuoteLineItem(
                      materialResp,
                      materialObjectQuery,
                      source,
                      quoteObj,
                      quoteObj.quote_line_items.data.length + 1
                    )
                  );
                });
            })
              .then(() => {
                if (scenario?.equipment?.r_motor) {
                  addingQuoteMessages.value.push(`Adding motor to quote.`);
                }
                return promiseMap(
                  [scenario.equipment.r_motor].filter(Boolean),
                  motor => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.MOTOR,
                        motor,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching motor material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_intakes?.length) {
                  addingQuoteMessages.value.push(`Adding intakes to quote.`);
                }
                return promiseMap(
                  scenario?.equipment?.r_xref_intakes,
                  intake => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.INTAKE,
                        intake,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching intake material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_seals?.length) {
                  addingQuoteMessages.value.push(`Adding seals to quote.`);
                }
                return promiseMap(scenario?.equipment?.r_xref_seals, seal => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      LineItemType.SEAL,
                      seal,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching seal material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      quoteObj.quote_line_items.data.push(
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          source,
                          quoteObj,
                          quoteObj.quote_line_items.data.length + 1
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_sensor) {
                  addingQuoteMessages.value.push(`Adding sensor to quote.`);
                }
                return promiseMap(
                  [scenario?.equipment?.r_sensor].filter(Boolean),
                  sensor => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.SENSOR,
                        sensor,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching sensor material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_mle) {
                  addingQuoteMessages.value.push(
                    `Adding motor lead extension to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_mle].filter(Boolean),
                  mle => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.MLE,
                        mle,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching motor lead extension material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_cables?.length) {
                  addingQuoteMessages.value.push(`Adding cables to quote.`);
                }
                return promiseMap(scenario?.equipment?.r_xref_cables, cable => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      LineItemType.CABLE,
                      cable,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching cable material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      quoteObj.quote_line_items.data.push(
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          source,
                          quoteObj,
                          quoteObj.quote_line_items.data.length + 1
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_switchboard) {
                  addingQuoteMessages.value.push(
                    `Adding switchboard/surface drive to quote.`
                  );
                }

                let switchboards = [scenario?.equipment?.r_switchboard].filter(
                  Boolean
                );

                if (scenario?.equipment?.use_vsd) {
                  switchboards = [];
                }

                return promiseMap(switchboards, switchboard => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      "switchboard",
                      switchboard,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching switchboard surface drive material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      quoteObj.quote_line_items.data.push(
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          source,
                          quoteObj,
                          quoteObj.quote_line_items.data.length + 1
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_vsd) {
                  addingQuoteMessages.value.push(
                    `Adding variable speed drive to quote.`
                  );
                }
                let vsds = [scenario?.equipment?.r_vsd].filter(Boolean);

                if (!scenario?.equipment?.use_vsd) {
                  vsds = [];
                }

                return promiseMap(vsds, vsd => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      "vsd",
                      vsd,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching variable speed drive material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      quoteObj.quote_line_items.data.push(
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          source,
                          quoteObj,
                          quoteObj.quote_line_items.data.length + 1
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_step_up_transformer) {
                  addingQuoteMessages.value.push(
                    `Adding Step Up Transformer to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_step_up_transformer].filter(Boolean),
                  step_up_transformer => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.STEP_UP_TRANSFORMER,
                        step_up_transformer,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching step up transformer material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_step_down_transformer) {
                  addingQuoteMessages.value.push(
                    `Adding Step Down Transformer to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_step_down_transformer].filter(
                    Boolean
                  ),
                  step_down_transformer => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.STEP_DOWN_TRANSFORMER,
                        step_down_transformer,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching step down transformer material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        quoteObj.quote_line_items.data.push(
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            source,
                            quoteObj,
                            quoteObj.quote_line_items.data.length + 1
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                const line_items = quoteObj.quote_line_items.data;
                const mappedLineItems = Object.groupBy(
                  line_items,
                  ({ line_item_type }) => line_item_type
                );
                quoteObj.quote_line_items.data = line_item_order
                  .map(line_item_type => mappedLineItems[line_item_type])
                  .flat()
                  .filter(Boolean)
                  .map((line_item, index) => {
                    line_item.sub_order = index + 1;
                    return line_item;
                  });
                return quoteObj;
              });
          });
        });
      });
      return Promise.all(quotes);
    })
    .then(qs => {
      addingQuoteMessages.value.push(`Saving Quote.`);
      scenariosToCreateQuotes.value = [];
      return insertQuote({ object: qs[0] });
    })
    .then(____resp => {
      const quoteId = ____resp?.data?.insert_quotes_one?.id;
      if (should_route) {
        const routeData = router.resolve({
          name: "EditQuote",
          params: {
            quoteId
          }
        });
        window.open(routeData.href, "_blank");
      }
      if (!quoteId) {
        throw new Error(`We had a problem saving the quote. Contact support.`);
      }
    })
    .then(refetchQuoteScenariosBySizingQuery)
    .then(() => {
      addingQuoteMessages.value = [];
    })
    .catch(error => {
      console.error(error);
      addingQuoteMessages.value.push(error.message);
    });
};

// when 'Select All' checkbox is toggled, this switches all checkboxes on or off to match its state
const toggleAllBulkSelects = () => {
  if (quoteScenariosBySizingQuery.value?.scenarios) {
    if (allScenarioSelect?.value[0] === "all") {
      const selects = [];
      for (const s of quoteScenariosBySizingQuery.value?.scenarios) {
        selects.push(s);
      }
      selectedScenarios.value = [...selects];
    } else {
      selectedScenarios.value = [];
    }
  }
};

// automatically check 'Select All' if all boxes are checked, or uncheck it if not
const updateSelectAllCheckbox = () => {
  if (quoteScenariosBySizingQuery.value?.scenarios) {
    if (
      selectedScenarios?.value.length ===
      quoteScenariosBySizingQuery.value?.scenarios.length
    ) {
      allScenarioSelect.value = ["all"];
    } else {
      allScenarioSelect.value = [];
    }
  }
};

// expands all scenarios when data is loaded
const expandedKeys = computed(() => {
  if (quoteScenariosBySizingQuery.value?.scenarios) {
    const dataArray = quoteScenariosBySizingQuery.value?.scenarios;
    const keys = {};
    for (const i in dataArray) {
      keys[i] = true;
    }
    return keys;
  }
  return {};
});

// adds scenario and quote data to treeNodes format
const treeNodes = computed(() => {
  if (quoteScenariosBySizingQuery.value?.scenarios) {
    const dataArray = quoteScenariosBySizingQuery.value?.scenarios;
    const nodes = [];
    for (const i in dataArray) {
      const children = [];
      for (const j in dataArray[i].quotes) {
        children.push({
          key: i.toString() + "-" + j.toString(),
          data: {
            type: "quote",
            data: dataArray[i].quotes[j],
            preparedBy: getUserDisplayName(
              dataArray[i].quotes[j].prepared_by_id
            )
          }
        });
      }

      nodes.push({
        key: i.toString(),
        data: {
          type: "scenario",
          data: dataArray[i],
          preparedBy: getUserDisplayName(dataArray[i].journal_author_id)
        },
        children
      });
    }
    return nodes;
  }
  return [];
});

const { data: userItems, fetching: fetchingUsers } = useUsersQuery({
  requestPolicy: "network-only",
  variables: {}
});
const getUserDisplayName = id => {
  if (userItems?.value?.users) {
    for (const user of userItems?.value?.users) {
      if (user.id === id) return user.display_name;
    }
  }
  return id;
};
</script>

<style lang="scss" scoped>
.loading-spinner {
  border: 2px solid $spyglass-blue;
  border-top: 2px solid var(--surface-400);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  animation: spin 2s linear infinite;
  margin-right: 16px;

  display: inline;
  float: left;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
